import { useState, useEffect } from "react";
import { Lock } from "lucide-react";
import { Drawer } from "../primitives/Drawer";
import { Switch } from "../primitives/Switch";
import { Button } from "../primitives/Button";
import { Tabs } from "../primitives/Tabs";
import { ROLE_OPTIONS, listCustomRoles, createCustomRole, updateCustomRole, type CustomRole } from "../../api/admin";
import {
  AVAILABLE_PERMISSIONS,
  getDefaultPermissions,
  getEffectivePermissions,
  getRoleDescription,
  type PermissionOverride,
} from "../../utils/permissions";
import { CustomRoleModal } from "./CustomRoleModal";
import { cn } from "../utils/cn";

type Membership = {
  id: string;
  organization_id: string;
  org_role: string | null;
  custom_role_id: string | null;
  status: string;
  permissions_override?: PermissionOverride | null;
  organization?: {
    name: string;
    type: string;
  };
  custom_role?: CustomRole | null;
};

type EditMembershipDrawerProps = {
  membership: Membership | null;
  open: boolean;
  onClose: () => void;
  onSave: (
    membershipId: string,
    updates: {
      org_role?: string | null;
      custom_role_id?: string | null;
      status?: string;
    }
  ) => Promise<void>;
};

export function EditMembershipDrawer({ membership, open, onClose, onSave }: EditMembershipDrawerProps) {
  const [activeTab, setActiveTab] = useState<"permissions" | "farms">("permissions");
  const [selectedRoleType, setSelectedRoleType] = useState<"system" | "custom">("system");
  const [selectedSystemRole, setSelectedSystemRole] = useState<string>("operator");
  const [selectedCustomRoleId, setSelectedCustomRoleId] = useState<string | null>(null);
  const [customRoles, setCustomRoles] = useState<CustomRole[]>([]);
  const [status, setStatus] = useState<boolean>(true);
  const [saving, setSaving] = useState(false);
  const [customRoleModalOpen, setCustomRoleModalOpen] = useState(false);
  const [editingCustomRole, setEditingCustomRole] = useState<CustomRole | null>(null);
  const [creatingFromBaseRole, setCreatingFromBaseRole] = useState<string | null>(null);

  // Load custom roles when drawer opens
  useEffect(() => {
    if (open && membership?.organization_id) {
      loadCustomRoles();
    }
  }, [open, membership?.organization_id]);

  // Initialize state from membership
  useEffect(() => {
    if (membership) {
      setStatus(membership.status === "active");
      
      if (membership.custom_role_id) {
        setSelectedRoleType("custom");
        setSelectedCustomRoleId(membership.custom_role_id);
        setSelectedSystemRole(membership.org_role || "ops");
      } else {
        setSelectedRoleType("system");
        setSelectedSystemRole(membership.org_role || "ops");
        setSelectedCustomRoleId(null);
      }
    }
  }, [membership]);

  const loadCustomRoles = async () => {
    if (!membership?.organization_id) return;
    try {
      const roles = await listCustomRoles(membership.organization_id);
      setCustomRoles(roles.filter((r) => r.is_active));
    } catch (error) {
      console.error("Failed to load custom roles:", error);
    }
  };

  const handleRoleTypeChange = (type: "system" | "custom") => {
    setSelectedRoleType(type);
    if (type === "system") {
      setSelectedCustomRoleId(null);
    } else {
      setSelectedSystemRole("operator");
    }
  };

  const handleSystemRoleSelect = (role: string) => {
    setSelectedSystemRole(role);
    setSelectedCustomRoleId(null);
  };

  const handleCustomRoleSelect = (roleId: string | null) => {
    setSelectedCustomRoleId(roleId);
  };

  const handleCreateCustomRole = (baseRole?: string) => {
    setEditingCustomRole(null);
    setCreatingFromBaseRole(baseRole || null);
    setCustomRoleModalOpen(true);
  };

  const handleEditCustomRole = (role: CustomRole) => {
    setEditingCustomRole(role);
    setCreatingFromBaseRole(null);
    setCustomRoleModalOpen(true);
  };

  const handleSaveCustomRole = async (payload: any) => {
    if (!membership?.organization_id) return;
    
    try {
      if (editingCustomRole) {
        // Update existing role
        await updateCustomRole(editingCustomRole.id, payload);
      } else {
        // Create new role
        await createCustomRole(membership.organization_id, payload);
      }
      await loadCustomRoles();
      setCustomRoleModalOpen(false);
      setEditingCustomRole(null);
      setCreatingFromBaseRole(null);
    } catch (error) {
      console.error("Failed to save custom role:", error);
      throw error;
    }
  };

  const handleSave = async () => {
    if (!membership) return;
    setSaving(true);
    try {
      await onSave(membership.id, {
        org_role: selectedRoleType === "system" ? selectedSystemRole : null,
        custom_role_id: selectedRoleType === "custom" ? selectedCustomRoleId : null,
        status: status ? "active" : "suspended",
      });
      onClose();
    } catch (error) {
      console.error("Failed to save membership:", error);
      alert("Erreur lors de la sauvegarde. Veuillez réessayer.");
    } finally {
      setSaving(false);
    }
  };

  if (!membership) return null;

  const selectedCustomRole = customRoles.find((r) => r.id === selectedCustomRoleId);
  const selectedRoleInfo = selectedRoleType === "system" 
    ? getRoleDescription(selectedSystemRole)
    : selectedCustomRole
      ? { label: selectedCustomRole.name, description: selectedCustomRole.description || "", capabilities: [] }
      : null;

  // Check if organization is ambassador/xlkey/pilot (can manage farm access)
  const canManageFarms = membership.organization?.type === "ambassador_org" || 
                         membership.organization?.type === "xlkey_internal" ||
                         membership.organization?.type === "drone_pilot_org";

  return (
    <>
      <Drawer
        title={`Accès — ${membership.organization?.name || "Organisation"}`}
        open={open}
        onClose={onClose}
        size="lg"
        footer={
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-muted">
              Ces changements s'appliquent uniquement à cette organisation.
            </div>
            <div className="flex gap-2">
              <Button label="Annuler" intent="default" size="sm" onClick={onClose} disabled={saving} />
              <Button label="Enregistrer" intent="primary" size="sm" onClick={handleSave} disabled={saving} />
            </div>
          </div>
        }
      >
        <div className="space-y-6">
          <Tabs
            tabs={[
              { id: "permissions", label: "Permissions & Rôle" },
              ...(canManageFarms ? [{ id: "farms", label: "Accès aux fermes" }] : []),
            ]}
            activeId={activeTab}
            onChange={(id) => setActiveTab(id as "permissions" | "farms")}
            variant="underline"
          />

          {activeTab === "permissions" && (
            <div className="space-y-6">
              {/* SECTION 1 — Choix du rôle */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-text mb-1">Rôle</h3>
                  <p className="text-xs text-muted">
                    Choisissez un rôle système (verrouillé) ou un rôle personnalisé de l'organisation.
                  </p>
                </div>

                {/* Rôles système */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-muted" />
                    <span className="text-xs font-medium text-text">Rôles système (verrouillés)</span>
                  </div>
                  <p className="text-xs text-muted ml-6">
                    Rôles standards définis par XLKey. Non modifiables.
                  </p>
                  <div className="ml-6 space-y-2">
                    {ROLE_OPTIONS.map((role) => {
                      const roleInfo = getRoleDescription(role);
                      const isSelected = selectedRoleType === "system" && selectedSystemRole === role;
                      return (
                        <div
                          key={role}
                          className={cn(
                            "p-3 rounded-md border cursor-pointer transition",
                            isSelected
                              ? "border-primary bg-primary/5"
                              : "border-border bg-surface-muted hover:bg-surface"
                          )}
                          onClick={() => {
                            handleRoleTypeChange("system");
                            handleSystemRoleSelect(role);
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <div className={cn(
                              "w-4 h-4 rounded border-2 flex items-center justify-center",
                              isSelected ? "border-primary bg-primary" : "border-border"
                            )}>
                              {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                            </div>
                            <div className="flex-1">
                              <div className="text-sm font-medium text-text flex items-center gap-2">
                                <Lock className="w-3.5 h-3.5 text-muted" />
                                {roleInfo.label}
                              </div>
                              <div className="text-xs text-muted mt-0.5">{roleInfo.description}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Rôles personnalisés */}
                <div className="space-y-3 pt-4 border-t border-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-medium text-text">Rôles personnalisés (organisation)</span>
                      <p className="text-xs text-muted mt-0.5">
                        Rôles créés spécifiquement pour cette organisation.
                      </p>
                    </div>
                    <Button
                      label="Créer un rôle personnalisé"
                      intent="secondary"
                      size="sm"
                      onClick={() => handleCreateCustomRole()}
                    />
                  </div>
                  <div className="space-y-2">
                    {customRoles.length === 0 ? (
                      <div className="text-xs text-muted p-3 rounded-md border border-border bg-surface-muted text-center">
                        Aucun rôle personnalisé créé. Cliquez sur "Créer un rôle personnalisé" pour en ajouter un.
                      </div>
                    ) : (
                      customRoles.map((customRole) => {
                        const isSelected = selectedRoleType === "custom" && selectedCustomRoleId === customRole.id;
                        return (
                          <div
                            key={customRole.id}
                            className={cn(
                              "p-3 rounded-md border cursor-pointer transition group",
                              isSelected
                                ? "border-primary bg-primary/5"
                                : "border-border bg-surface-muted hover:bg-surface"
                            )}
                            onClick={() => {
                              handleRoleTypeChange("custom");
                              handleCustomRoleSelect(customRole.id);
                            }}
                          >
                            <div className="flex items-start gap-2">
                              <div className={cn(
                                "w-4 h-4 rounded border-2 flex items-center justify-center mt-0.5",
                                isSelected ? "border-primary bg-primary" : "border-border"
                              )}>
                                {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                              </div>
                              <div className="flex-1">
                                <div className="text-sm font-medium text-text">{customRole.name}</div>
                                <div className="text-xs text-muted mt-0.5">
                                  {customRole.description || "Aucune description"}
                                </div>
                                {customRole.base_role && (
                                  <div className="text-xs text-muted mt-1">
                                    Basé sur : {getRoleDescription(customRole.base_role).label}
                                  </div>
                                )}
                              </div>
                              <button
                                className="opacity-0 group-hover:opacity-100 text-xs text-primary hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditCustomRole(customRole);
                                }}
                              >
                                Modifier
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Aperçu du rôle sélectionné */}
                {selectedRoleInfo && (
                  <div className="rounded-md border border-border bg-surface-muted p-4 mt-4">
                    <div className="text-sm font-medium text-text mb-2">{selectedRoleInfo.label}</div>
                    <div className="text-xs text-muted mb-3">{selectedRoleInfo.description}</div>
                    {selectedRoleType === "system" && selectedRoleInfo.capabilities.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-xs font-medium text-text mb-2">Capacités par défaut :</div>
                        {selectedRoleInfo.capabilities.map((capability, idx) => (
                          <div key={idx} className="text-xs text-text flex items-start gap-2">
                            <span className="text-success mt-0.5">✓</span>
                            <span>{capability}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedRoleType === "custom" && selectedCustomRole && (
                      <div className="space-y-1">
                        <div className="text-xs font-medium text-text mb-2">Permissions :</div>
                        {Object.entries(selectedCustomRole.permissions)
                          .filter(([_, enabled]) => enabled === true)
                          .map(([permId]) => {
                            const permInfo = AVAILABLE_PERMISSIONS.find((p) => p.id === permId);
                            return (
                              <div key={permId} className="text-xs text-text flex items-start gap-2">
                                <span className="text-success mt-0.5">✓</span>
                                <span>{permInfo?.label || permId}</span>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* SECTION 2 — Statut d'accès */}
              <div className="space-y-3 pt-4 border-t border-border">
                <div>
                  <h3 className="text-sm font-semibold text-text mb-1">Statut d'accès</h3>
                  <p className="text-xs text-muted">
                    Désactiver l'accès bloque la connexion sans supprimer le rôle.
                  </p>
                </div>
                <div className="flex items-center justify-between p-4 rounded-md border border-border bg-surface-muted">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-text mb-1">
                      {status ? "Accès actif" : "Accès suspendu"}
                    </div>
                    <div className="text-xs text-muted">
                      {status
                        ? "L'utilisateur peut accéder à cette organisation et utiliser ses permissions."
                        : "L'utilisateur ne peut pas accéder à cette organisation, même avec des permissions valides."}
                    </div>
                  </div>
                  <Switch
                    label={status ? "Actif" : "Suspendu"}
                    checked={status}
                    onChange={setStatus}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === "farms" && canManageFarms && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-text mb-1">Accès aux fermes</h3>
                <p className="text-xs text-muted">
                  Gérez les accès aux fermes clients pour cet utilisateur dans le contexte de {membership.organization?.name}.
                </p>
              </div>
              <div className="rounded-md border border-border bg-surface-muted p-6 text-center">
                <div className="text-sm text-muted">
                  La gestion des accès aux fermes sera disponible prochainement.
                </div>
                <div className="text-xs text-muted mt-2">
                  Par défaut, l'utilisateur a accès à toutes les fermes de l'organisation {membership.organization?.name}.
                </div>
              </div>
            </div>
          )}
        </div>
      </Drawer>

      <CustomRoleModal
        open={customRoleModalOpen}
        onClose={() => {
          setCustomRoleModalOpen(false);
          setEditingCustomRole(null);
          setCreatingFromBaseRole(null);
        }}
        organizationId={membership.organization_id}
        role={editingCustomRole}
        baseRole={creatingFromBaseRole || selectedSystemRole}
        onSave={handleSaveCustomRole}
      />
    </>
  );
}
